import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, X, ArrowRight, FlaskConical } from "lucide-react";
import Seo from "@/components/Seo";
import { LIBRARY, RESEARCH_AREAS } from "@/data/researchLibrary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVIDENCE_COLORS = {
  "FOUNDATIONAL": "text-emerald-400 border-emerald-400/40",
  "EARLY TRANSLATIONAL": "text-sky-400 border-sky-400/40",
  "PRECLINICAL": "text-[#D4AF37] border-[#D4AF37]/40",
  "LIMITED / EMERGING": "text-amber-400 border-amber-400/40",
  "POORLY CHARACTERIZED": "text-zinc-400 border-zinc-600",
};

export default function ResearchLibrary() {
  const [entries, setEntries] = useState(LIBRARY); // static fallback shown immediately
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("All");
  const q = query.trim().toLowerCase();

  useEffect(() => {
    axios.get(`${API}/research`).then(({ data }) => {
      if (Array.isArray(data) && data.length) setEntries(data);
    }).catch(() => { /* keep static fallback */ });
  }, []);

  const results = useMemo(() => {
    let list = entries;
    if (area !== "All") list = list.filter((e) => (e.areas || []).includes(area));
    if (q) list = list.filter((e) =>
      (e.name + " " + (e.aliases || []).join(" ") + " " + (e.areas || []).join(" ") + " " +
        (e.targets || []).join(" ") + " " + (e.keywords || []).join(" ") + " " + e.classification).toLowerCase().includes(q));
    return list;
  }, [entries, q, area]);

  const areaChips = useMemo(() => {
    const s = new Set(RESEARCH_AREAS);
    entries.forEach((e) => (e.areas || []).forEach((a) => s.add(a)));
    return [...s].sort();
  }, [entries]);

  return (
    <main className="pt-32 pb-24" data-testid="research-library-page">
      <Seo title="Research Library" path="/library"
        description="Explore molecular pathways, compound classifications, research areas, and scientific literature associated with materials in the Molecularix catalog. For research use only." />
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Molecularix Research Library</motion.p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.02] mb-5">The Compound Knowledge Base</h1>
        <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-2xl mb-8">
          Explore molecular pathways, compound classifications, research areas, and scientific literature associated with materials in the Molecularix catalog. Educational and scientific reference only — not medical advice.
        </p>

        <div className="relative max-w-xl mb-8">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} type="search" aria-label="Search research library"
            placeholder="Search compounds, targets, pathways…" data-testid="library-search"
            className="w-full bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none pl-11 pr-11 py-3.5 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
          {query && (
            <button onClick={() => setQuery("")} aria-label="Clear search" data-testid="library-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#D4AF37]"><X size={16} /></button>
          )}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-1 px-1" role="tablist" aria-label="Research areas" data-testid="library-areas">
          {["All", ...areaChips].map((a) => (
            <button key={a} onClick={() => setArea(a)} role="tab" aria-selected={area === a}
              data-testid={`library-area-${a.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors whitespace-nowrap ${
                area === a ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"}`}>
              {a}
            </button>
          ))}
        </div>

        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-5" data-testid="library-count">
          {results.length} compound{results.length === 1 ? "" : "s"}
        </p>

        {results.length === 0 ? (
          <div className="border border-zinc-800 bg-[#0C0C0C] p-10 text-center" data-testid="library-empty">
            <p className="text-[#A1A1AA] text-sm mb-4">No compounds match your search.</p>
            <button onClick={() => { setQuery(""); setArea("All"); }}
              className="border border-zinc-700 text-[#A1A1AA] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Clear filters</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {results.map((e) => (
              <Link key={e.slug} to={`/library/${e.slug}`} data-testid={`library-card-${e.slug}`}
                className="group bg-[#0A0A0A] p-6 flex flex-col hover:bg-[#0F0F0F] transition-colors">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <FlaskConical size={16} className="text-[#D4AF37]" />
                  <span className={`font-mono text-[9px] uppercase tracking-[0.12em] px-2 py-0.5 border ${EVIDENCE_COLORS[e.evidence] || "text-zinc-400 border-zinc-600"}`}>{e.evidence}</span>
                </div>
                <h2 className="font-serif text-xl font-light mb-1 group-hover:text-[#D4AF37] transition-colors">{e.name}</h2>
                <p className="font-mono text-[10px] text-[#71717A] mb-3">{(e.areas || [])[0]}</p>
                <p className="text-[#71717A] text-[13px] leading-relaxed flex-1 line-clamp-3">{e.classification}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]">Read overview <ArrowRight size={12} /></span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
